import apiClient from './client';
import {
  calculateAggregateProgress,
  calculateUploadProgressPercent,
  chunkPartNumbers,
  createChunkRanges,
  getUploadMultipartConfig,
  recordPartProgress,
  type ChunkRange,
  type UploadMultipartConfig,
} from './chunked-upload';

export type DirectUploadBusinessType =
  | 'project_resource'
  | 'attachment'
  | 'storyboard_image'
  | 'screenplay_upload'
  | 'storyboard_import'
  | 'digital_human_primary_image'
  | 'visual_style_analysis_image';

interface DirectUploadInitiateResponse {
  sessionId: string;
  uploadMode: 'single' | 'multipart';
  objectKey: string;
  expiresAt: string;
  uploadUrl?: string | null;
  requiredHeaders?: Record<string, string>;
  uploadId?: string | null;
  partSize?: number | null;
}

interface DirectUploadCompleteResponse<T> {
  sessionId: string;
  businessType: string;
  result: T;
}

interface DirectUploadPartUrlResponse {
  parts: Array<{
    partNumber: number;
    uploadUrl: string;
  }>;
  expiresIn: number;
}

export interface DirectUploadOptions {
  file: File;
  businessType: DirectUploadBusinessType;
  businessPayload: Record<string, unknown>;
  multipartConfig?: UploadMultipartConfig;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

const PART_URL_BATCH_SIZE = 1000;
const COMPLETE_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;
const OSS_PUT_TIMEOUT_MS = COMPLETE_UPLOAD_TIMEOUT_MS;

export async function uploadDirectToOss<T = Record<string, unknown>>(
  options: DirectUploadOptions
): Promise<T> {
  const multipartConfig = options.multipartConfig ?? (await getUploadMultipartConfig());
  const initiate = (await apiClient.post('/uploads/initiate', {
    fileName: options.file.name,
    fileSize: options.file.size,
    mimeType: options.file.type || 'application/octet-stream',
    businessType: options.businessType,
    businessPayload: options.businessPayload,
  })) as DirectUploadInitiateResponse;

  try {
    if (initiate.uploadMode === 'single') {
      if (!initiate.uploadUrl) {
        throw new Error('缺少单文件直传地址');
      }
      await putObject({
        file: options.file,
        uploadUrl: initiate.uploadUrl,
        headers: initiate.requiredHeaders ?? {},
        signal: options.signal,
        onProgress: (loaded, total) => {
          options.onProgress?.(calculateUploadProgressPercent(loaded, total || options.file.size));
        },
      });
      options.onProgress?.(100);
      const completed = (await apiClient.post(
        `/uploads/${initiate.sessionId}/complete`,
        {},
        { timeout: COMPLETE_UPLOAD_TIMEOUT_MS }
      )) as DirectUploadCompleteResponse<T>;
      return completed.result;
    }

    const result = await uploadMultipart<T>({
      initiate,
      file: options.file,
      multipartConfig,
      signal: options.signal,
      onProgress: options.onProgress,
    });
    return result;
  } catch (error) {
    await Promise.resolve(apiClient.post(`/uploads/${initiate.sessionId}/abort`)).catch(
      () => undefined
    );
    throw error;
  }
}

async function uploadMultipart<T>({
  initiate,
  file,
  multipartConfig,
  signal,
  onProgress,
}: {
  initiate: DirectUploadInitiateResponse;
  file: File;
  multipartConfig: UploadMultipartConfig;
  signal?: AbortSignal;
  onProgress?: (percent: number) => void;
}): Promise<T> {
  const partSize = initiate.partSize ?? multipartConfig.chunkSizeBytes;
  const ranges = createChunkRanges(file.size, partSize);
  const uploadUrlByPartNumber = await fetchPartUrls(
    initiate.sessionId,
    ranges.map((range) => range.partNumber)
  );

  const partProgress = new Map<number, number>();
  const completedParts: Array<{ partNumber: number; etag: string }> = [];
  let nextIndex = 0;

  const reportPartProgress = (range: ChunkRange, loaded: number) => {
    recordPartProgress(partProgress, range, loaded);
    onProgress?.(Math.min(99, calculateAggregateProgress(partProgress, file.size)));
  };

  const worker = async () => {
    while (nextIndex < ranges.length) {
      const range = ranges[nextIndex];
      nextIndex += 1;
      const uploadUrl = uploadUrlByPartNumber.get(range.partNumber);
      if (!uploadUrl) {
        throw new Error(`缺少分片上传地址: partNumber=${range.partNumber}`);
      }
      const etag = await putPartWithRetry({
        file,
        range,
        uploadUrl,
        signal,
        onProgress: (loaded) => reportPartProgress(range, loaded),
        refreshUploadUrl: async () => {
          const refreshedUrls = await fetchPartUrls(initiate.sessionId, [range.partNumber]);
          const refreshedUrl = refreshedUrls.get(range.partNumber);
          if (!refreshedUrl) {
            throw new Error(`缺少刷新后的分片上传地址: partNumber=${range.partNumber}`);
          }
          uploadUrlByPartNumber.set(range.partNumber, refreshedUrl);
          return refreshedUrl;
        },
      });
      reportPartProgress(range, range.end - range.start);
      completedParts.push({ partNumber: range.partNumber, etag });
    }
  };

  const concurrency = Math.min(Math.max(1, multipartConfig.maxConcurrency), ranges.length);
  await Promise.all(Array.from({ length: concurrency }, () => worker()));
  completedParts.sort((a, b) => a.partNumber - b.partNumber);
  onProgress?.(100);
  const completed = (await apiClient.post(
    `/uploads/${initiate.sessionId}/complete`,
    { parts: completedParts },
    { timeout: COMPLETE_UPLOAD_TIMEOUT_MS }
  )) as DirectUploadCompleteResponse<T>;
  return completed.result;
}

async function fetchPartUrls(
  sessionId: string,
  partNumbers: number[]
): Promise<Map<number, string>> {
  const uploadUrlByPartNumber = new Map<number, string>();
  for (const batch of chunkPartNumbers(partNumbers, PART_URL_BATCH_SIZE)) {
    const partUrls = (await apiClient.post(`/uploads/${sessionId}/parts`, {
      partNumbers: batch,
    })) as DirectUploadPartUrlResponse;
    partUrls.parts.forEach((part) => {
      uploadUrlByPartNumber.set(part.partNumber, part.uploadUrl);
    });
  }
  return uploadUrlByPartNumber;
}

async function putPartWithRetry({
  file,
  range,
  uploadUrl,
  signal,
  onProgress,
  refreshUploadUrl,
}: {
  file: File;
  range: ChunkRange;
  uploadUrl: string;
  signal?: AbortSignal;
  onProgress: (loaded: number) => void;
  refreshUploadUrl: () => Promise<string>;
}): Promise<string> {
  let lastError: unknown;
  let currentUploadUrl = uploadUrl;
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    try {
      return await putObject({
        file: file.slice(range.start, range.end),
        uploadUrl: currentUploadUrl,
        headers: {},
        signal,
        onProgress: (loaded) => onProgress(loaded),
        requireEtag: true,
      });
    } catch (error) {
      lastError = error;
      if (signal?.aborted) break;
      if (isHttpStatusError(error, 403)) {
        currentUploadUrl = await refreshUploadUrl();
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error('分片上传 OSS 失败');
}

function putObject({
  file,
  uploadUrl,
  headers,
  signal,
  onProgress,
  requireEtag = false,
}: {
  file: Blob;
  uploadUrl: string;
  headers: Record<string, string>;
  signal?: AbortSignal;
  onProgress: (loaded: number, total: number) => void;
  requireEtag?: boolean;
}): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const cleanup = () => {
      signal?.removeEventListener('abort', abort);
    };
    const abort = () => {
      xhr.abort();
      reject(new DOMException('上传已取消', 'AbortError'));
    };
    if (signal?.aborted) {
      abort();
      return;
    }
    signal?.addEventListener('abort', abort, { once: true });
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded, event.total);
      }
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status < 200 || xhr.status >= 300) {
        const error = new Error(`上传 OSS 失败: HTTP ${xhr.status}`) as Error & { status?: number };
        error.status = xhr.status;
        reject(error);
        return;
      }
      const etag = xhr.getResponseHeader('ETag') ?? '';
      if (requireEtag && !etag) {
        reject(new Error('对象存储未返回 ETag，请检查 CORS 暴露配置'));
        return;
      }
      resolve(etag);
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error('上传 OSS 网络错误'));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException('上传已取消', 'AbortError'));
    };
    xhr.ontimeout = () => {
      cleanup();
      reject(
        new Error(
          '上传 OSS 超时，请检查网络后重试'
        )
      );
    };
    xhr.open('PUT', uploadUrl);
    xhr.timeout = OSS_PUT_TIMEOUT_MS;
    Object.entries(headers).forEach(([name, value]) => {
      xhr.setRequestHeader(name, value);
    });
    xhr.send(file);
  });
}

function isHttpStatusError(error: unknown, status: number): boolean {
  return (
    error instanceof Error && 'status' in error && (error as { status?: number }).status === status
  );
}
